import React, { Component } from 'react';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Elements
import AvailableCitiesModal from '../elements/available-cities-modal';

// Premium components
import {
  PremiumHero,
  SectionWrapper,
  FaqAccordion,
  FinalCTA,
  StructuredData,
} from '../elements/premium';
import { startingPriceLabel, getServiceStartingPrice } from '../../utils/pricing';

const COMPARISON_ROWS = [
  { feature: 'Service Type', rnr: 'Doorstep (mechanic comes to you)', gm: 'Workshop + limited doorstep' },
  { feature: 'Bike Service', rnr: '32+ cities, from \u20B9450', gm: '2Wheels launched Feb 2025, 7 cities', rnrGood: true, gmWarn: true },
  { feature: 'Car Service', rnr: 'From \u20B92,499', gm: 'From \u20B92,499', rnrGood: true, gmGood: true },
  { feature: 'Arrival Time', rnr: '15 minutes avg', gm: 'Workshop appointment' },
  { feature: 'Warranty', rnr: '30 days parts & labour', gm: '1 month' },
  { feature: 'Cities', rnr: '32+', gm: '40+ (workshop-based)' },
  { feature: 'Real-time Updates', rnr: 'Photos + WhatsApp', gm: 'Not available', rnrGood: true, gmWarn: true },
  { feature: 'Customers Served', rnr: '2,00,000+', gm: '\u2014' },
];

const FAQS = [
  {
    q: 'Is Ride N Repair better than GoMechanic for bikes?',
    a: 'For bike service, Ride N Repair is the stronger choice. We operate doorstep bike service in 32+ cities with mechanics arriving within 15 minutes. GoMechanic launched its 2Wheels bike vertical only in February 2025 and currently covers just 7 cities. Our bike service starts at \u20B9450 with 30-day warranty on parts and labour.',
  },
  {
    q: 'Does GoMechanic offer doorstep bike service?',
    a: 'GoMechanic is primarily workshop-based. Their 2Wheels programme offers limited doorstep coverage in select cities. Ride N Repair was built doorstep-first\u2014every service, bike or car, is performed at your location by a fully equipped mechanic.',
  },
  {
    q: 'What is the difference between GoMechanic and Ride N Repair pricing?',
    a: 'GoMechanic lists bike general service from \u20B9299, but this is a workshop drop-off price. Ride N Repair starts at \u20B9450 for doorstep service\u2014the mechanic comes to your home or office, saving you the trip and waiting time. For cars, both platforms start around \u20B92,499. Factor in commute time and convenience, and doorstep service often works out more cost-effective.',
  },
  {
    q: 'Which has better coverage \u2014 GoMechanic or Ride N Repair?',
    a: 'GoMechanic has 40+ cities for car workshops. However, for bikes, their 2Wheels programme is active in only 7 cities. Ride N Repair covers 32+ cities for both bike and car doorstep service, making us the better option if you need a mechanic to come to you.',
  },
  {
    q: 'Is GoMechanic 2Wheels available in my city?',
    a: 'As of 2026, GoMechanic 2Wheels is available in approximately 7 cities. Ride N Repair serves 32+ cities with doorstep bike and car service. Check our booking page to see if your city is covered\u2014most metro and tier-2 cities are already live.',
  },
];

class GoMechanicComparison extends Component {
  render() {
    const { isMobileBrowser, isMobileApp, user, setUser, citiesModal, setCitiesModal } = this.props;

    return (
      <>
        <Helmet>
          <title>GoMechanic vs Ride N Repair 2026 — Honest Comparison | Bikes &amp; Cars</title>
          <meta
            name="description"
            content="GoMechanic vs Ride N Repair — side-by-side comparison of pricing, services, cities, doorstep availability, warranty. Which is better for bikes and cars in 2026?"
          />
          <meta
            name="keywords"
            content="gomechanic vs ride n repair, gomechanic bike service, go mechanic bike service, gomechanic alternative, gomechanic 2wheels, doorstep bike service"
          />
          <link rel="canonical" href="https://www.ridenrepair.com/gomechanic-vs-ride-n-repair" />
        </Helmet>

        <StructuredData
          data={{
            '@type': 'WebPage',
            name: 'GoMechanic vs Ride N Repair 2026 — Honest Comparison',
            description: 'Side-by-side comparison of GoMechanic and Ride N Repair for bike and car services in India.',
            url: 'https://www.ridenrepair.com/gomechanic-vs-ride-n-repair',
          }}
        />

        {citiesModal && (
          <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />
        )}

        <Header
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          user={user}
          setUser={setUser}
        />

        <PremiumHero
          badge="Honest Comparison 2026"
          title="GoMechanic vs Ride N Repair — Which Doorstep Service Wins?"
          subtitle="A transparent, fact-based comparison to help you choose the right vehicle service platform."
          compact
          isMobileBrowser={isMobileBrowser}
          ctas={[
            { label: 'Book Doorstep Service', href: '/booking', variant: 'primary' },
            { label: 'View Pricing', href: '/service', variant: 'secondary' },
          ]}
        />

        {/* Comparison Table */}
        <SectionWrapper
          id="comparison"
          kicker="Feature Comparison"
          heading="Ride N Repair vs GoMechanic — Side by Side"
          subtitle="Every feature compared honestly so you can make an informed decision."
        >
          <div className="rr-competitor-table__wrap" style={{ overflowX: 'auto', marginTop: '2rem' }}>
            <table className="rr-comparison-table" style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '1rem',
              lineHeight: '1.6',
            }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Feature</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700, color: '#e63946' }}>Ride N Repair</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>GoMechanic</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                    <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #eee', fontWeight: 600 }}>
                      {row.feature}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #eee' }}>
                      {row.rnrGood && <span style={{ marginRight: '0.4rem' }}>{'\u2705'}</span>}
                      {row.rnr}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #eee' }}>
                      {row.gmWarn && <span style={{ marginRight: '0.4rem' }}>{'\u26A0\uFE0F'}</span>}
                      {row.gmGood && <span style={{ marginRight: '0.4rem' }}>{'\u2705'}</span>}
                      {row.gm}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionWrapper>

        {/* Why Ride N Repair for Bikes */}
        <SectionWrapper
          id="bikes"
          variant="gray"
          kicker="Bikes"
          heading="Why Ride N Repair for Bikes"
          subtitle="GoMechanic started as a car-focused platform. Bike service is our core strength."
        >
          <div style={{ maxWidth: 800, margin: '0 auto', lineHeight: '1.8' }}>
            <p>
              GoMechanic launched its two-wheeler division, <strong>2Wheels</strong>, in February 2025.
              As of 2026, it covers roughly 7 cities with limited doorstep options. Most bike services
              still require you to drop your vehicle at a partner workshop.
            </p>
            <p>
              <strong>Ride N Repair</strong> has served <strong>2,00,000+ customers</strong> across{' '}
              <strong>32+ cities</strong> with 100% doorstep service. Every mechanic arrives fully equipped
              within 15 minutes on average. You never need to leave your home or office.
            </p>
            <ul style={{ paddingLeft: '1.5rem' }}>
              <li>General bike service from <strong>{'\u20B9'}450</strong> at your doorstep</li>
              <li>Repairs, oil changes, brake pads, battery, tyres - all at your location</li>
              <li>30-day warranty on parts and labour</li>
              <li>Real-time updates with photos via WhatsApp</li>
              <li>Transparent pricing - no hidden workshop fees</li>
            </ul>
          </div>
        </SectionWrapper>

        {/* Pricing Comparison */}
        <SectionWrapper
          id="pricing"
          kicker="Pricing"
          heading="Pricing Comparison"
          subtitle="Lower sticker price does not always mean lower total cost."
        >
          <div style={{ maxWidth: 800, margin: '0 auto', lineHeight: '1.8' }}>
            <p>
              GoMechanic lists general bike service starting at <strong>{'\u20B9'}299</strong>. However,
              this is a workshop drop-off price. You need to ride or transport your bike to a partner garage,
              wait or return later, then pick it up. The total time investment is typically 3-5 hours.
            </p>
            <p>
              Ride N Repair doorstep bike service starts at <strong>{'\u20B9'}450</strong>. The mechanic
              comes to your location, performs the service in 45-90 minutes, and you carry on with your day.
              When you factor in commute time, fuel, and productivity lost, doorstep service is often more
              cost-effective.
            </p>
            <div style={{
              background: '#f0f7ff',
              border: '1px solid #c8ddf5',
              borderRadius: 8,
              padding: '1.5rem',
              marginTop: '1.5rem',
            }}>
              <p style={{ fontWeight: 700, marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                For car services:
              </p>
              <p>
                Both platforms offer general car service from approximately <strong>{'\u20B9'}2,499</strong>.
                GoMechanic has a wide workshop network for cars. Ride N Repair offers the same services at
                your doorstep with the added convenience of zero commute.
              </p>
            </div>
          </div>
        </SectionWrapper>

        {/* FAQ */}
        <SectionWrapper
          id="faq"
          variant="gray"
          kicker="Questions"
          heading="Frequently Asked Questions"
          subtitle="Common questions about GoMechanic vs Ride N Repair."
        >
          <FaqAccordion faqs={FAQS} withSchema />
        </SectionWrapper>

        <FinalCTA
          heading="Book Doorstep Service Now"
          subtitle={`Bike service from ${startingPriceLabel('bike')} | Car service from ${getServiceStartingPrice('car-service').label} | 32+ cities | 15-minute arrival`}
          ctas={[
            { label: 'Book Now \u2014 From \u20B9450', href: '/booking', variant: 'primary' },
            { label: 'See All Services', href: '/service', variant: 'white-outline' },
          ]}
        />

        <Footer
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          setCitiesModal={setCitiesModal}
        />
      </>
    );
  }
}

export default GoMechanicComparison;
